import { useEffect, useRef } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { CloseCircleOutline } from '@aslanpay/portal-common-lib';

import Step from 'app/components/UI/Step';

import useStep from 'app/hooks/useStep';

import { useCreateEmployeeStore, useNavigationStore } from 'app/store';

import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchPayCalendarsForEmployee from 'app/hooks/useFetchPayCalendarsForEmployee';

import {
  EmployeeCompanyForm,
  EmployeeDetailsForm,
  EmployeePayCalendarForm,
  EmployeeSalaryForm,
} from '../CreateEmployeeForm';
import { EmployeeCompanyFormFields, EmployeeFormFields } from '../CreateEmployeeForm/types';

import { CreateEmployeeModalProps } from './types';

function CreateEmployeeModal({ companyId, isOpen, onClose }: CreateEmployeeModalProps) {
  const { view } = useNavigationStore.getState();

  const { user } = useAuthContext();
  const { data: company } = useFetchCompany(companyId || user?.companyId);

  const { data: payCalendars } = useFetchPayCalendarsForEmployee([
    companyId || user?.companyId || '',
  ]);
  const hasOnlyOnePayCalendar = payCalendars?.list.length === 1;

  const employeeData = useCreateEmployeeStore((state) => state.employeeData);
  const setEmployeeData = useCreateEmployeeStore((state) => state.setEmployeeData);
  const resetEmployeeData = useCreateEmployeeStore((state) => state.resetEmployeeData);
  const initializeEmployeeData = useCreateEmployeeStore((state) => state.initializeEmployeeData);

  const buttonsRef = useRef<HTMLElement>(null);

  // Initialize employee data with company configuration when modal opens
  useEffect(() => {
    if (isOpen && companyId && !(employeeData as EmployeeCompanyFormFields).companyId) {
      initializeEmployeeData(companyId);
    }
  }, [isOpen, companyId, employeeData, setEmployeeData, initializeEmployeeData]);

  // Auto-set pay calendar when there is only one
  useEffect(() => {
    if (hasOnlyOnePayCalendar && payCalendars?.list[0]) {
      setEmployeeData({ payCalendarId: payCalendars.list[0].id } as EmployeeFormFields);
    }
  }, [hasOnlyOnePayCalendar, payCalendars?.list, setEmployeeData]);

  const steps = [
    ...(view === 'global' || !company?.id
      ? [
          {
            id: 'employee-company-form',
            name: 'Company selection',
          },
        ]
      : []),
    {
      id: 'employee-details-form',
      name: 'Personal details',
    },
    {
      id: 'employee-pay-calendar-form',
      name: 'Pay calendar',
    },
    {
      id: 'employee-salary-form',
      name: 'Salary details',
    },
  ];

  const { currentStep, nextStep, previousStep, canPrevStep, resetSteps } = useStep(steps.length);

  const handleClose = () => {
    resetEmployeeData();
    resetSteps();
    onClose();
  };

  const renderCurrentStepForm = () => {
    switch (steps[currentStep].id) {
      case 'employee-pay-calendar-form':
        return (
          <EmployeePayCalendarForm
            id={steps[currentStep].id}
            ref={buttonsRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'employee-salary-form':
        return (
          <EmployeeSalaryForm
            id={steps[currentStep].id}
            ref={buttonsRef}
            onStepAction={handleClose}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'employee-details-form':
        return (
          <EmployeeDetailsForm
            id={steps[currentStep].id}
            ref={buttonsRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'employee-company-form':
      default:
        return (
          <EmployeeCompanyForm
            id={steps[currentStep].id}
            ref={buttonsRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Create a new employee</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create employee"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="40px">
            <HStack spacing="0" flexGrow={1} alignItems="flex-start">
              {steps.map((step, id) => (
                <Step
                  key={step.name}
                  number={id + 1}
                  name={step.name}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={steps.length === id + 1}
                />
              ))}
            </HStack>
            {renderCurrentStepForm()}
          </Stack>
        </ModalBody>
        <ModalFooter ref={buttonsRef}>
          {currentStep !== steps.length - 1 && (
            <Text color="system.error" textStyle="extraSmallMedium">
              * mandatory
            </Text>
          )}
          <Spacer />
          <Button mr="20px" variant="link" size="sm" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateEmployeeModal;
